import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.tsx'
import useWordDetailsModalStore, { OnSuccessfulFinalActionMode } from '@/useWordDetailsModalStore.ts'
import {
	BookmarkCheck,
	FilePlus2,
	Info,
	Loader2,
	Pencil,
	Shapes,
	Sparkles,
	Trash,
	Undo2,
	Volume2,
	X,
} from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button.tsx'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn, pronounce } from '@/lib/utils.ts'
import { toast } from 'sonner'
import useGlobalStore from '@/useGlobalStore.ts'
import dayjs from 'dayjs'
import api from '@/api.ts'
import { SheetDescription } from '@/components/ui/sheet.tsx'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { FloatingInput, FloatingLabel } from '@/components/ui/floating-input.tsx'
import SuggestionFloatingInput from '@/components/ui/suggestion-floating-input.tsx'
import LearningStageCircle from '@/components/LearningStageCircle.tsx'
import { LibraryVocabulary } from '@/models.ts'
import { dispatch, useBus } from '@/useBus.ts'
import { Checkbox } from '@/components/ui/checkbox.tsx'

const WordDetailsDialog = () => {
	const { t } = useTranslation()
	const {
		learningLanguageCode,
		isTTSSupported,
		appLanguageCode,
		isTrialExpired,
		availableAppLanguages,
		libraryCollectionsList,
		loadLibraryCollectionsList,
		loadLibraryOverview,
	} = useGlobalStore()
	const { mode, setMode, isOpen, data, setData, hideModal, onSuccessfulFinalAction } = useWordDetailsModalStore()
	const [isDeleting, setIsDeleting] = useState(false)
	const [isSubmittingNewWord, setIsSubmittingNewWord] = useState(false)
	const [wordTappedForDeletion, setWordTappedForDeletion] = useState(false)
	const [createdAt, setCreatedAt] = useState('')
	const [newWord, setNewWord] = useState('')
	const [newWordTranslation, setNewWordTranslation] = useState('')
	const [newWordCollectionIds, setNewWordCollectionIds] = useState<number[]>([])
	const [isLoadingAiTranslation, setIsLoadingAiTranslation] = useState(false)
	const prevNewWord = useRef('')
	const [translationSuggestions, setTranslationSuggestions] = useState<string[]>([])
	const [loadingCollectionIds, setLoadingCollectionIds] = useState<number[]>([])
	const [showAllCollections, setShowAllCollections] = useState(false)
	const selectedCollectionIds = useMemo(() => {
		const set = new Set<number>()
		if (data?.collections) {
			for (const c of data.collections) set.add(c.id)
		}
		return set
	}, [data?.collections])
	const sortedCollections = useMemo(() => {
		return [...libraryCollectionsList].sort((a, b) => {
			const aSelected = selectedCollectionIds.has(a.id) ? 1 : 0
			const bSelected = selectedCollectionIds.has(b.id) ? 1 : 0
			if (aSelected !== bSelected) return bSelected - aSelected // selected first

			const aTime = new Date(a.updated_at).getTime()
			const bTime = new Date(b.updated_at).getTime()
			if (aTime !== bTime) return bTime - aTime // newer first

			// stable-ish final tie-break
			return (a.name || '').localeCompare(b.name || '')
		})
	}, [libraryCollectionsList, selectedCollectionIds])
	const hasMoreCollections = sortedCollections.length > 4
	const visibleCollections = useMemo(
		() => (showAllCollections ? sortedCollections : sortedCollections.slice(0, 4)),
		[sortedCollections, showAllCollections],
	)

	const pronounceText = async (word: string) => {
		if (!isTTSSupported) {
			toast(t('Speaking is not supported for this language.'))
			return
		}
		const text = word?.trim()
		if (!text) return
		await pronounce(text, learningLanguageCode)
	}

	const description = useMemo(() => {
		if (data === null) return ''
		if (data.learning_stage >= 0 && data.learning_stage < 3) {
			return t('Known')
		} else if (data.learning_stage < 0) {
			return t('Needs Work')
		} else {
			return t('Learned')
		}
	}, [data, t])

	useEffect(() => {
		if (mode === 'add/prefill' && data !== null && data.word !== '') {
			setNewWord(data.word)
			setNewWordTranslation(data.translation ?? '')
		}
	}, [data, mode])

	useEffect(() => {
		const targetDate = dayjs(data?.created_at)
		if (dayjs().diff(targetDate, 'day') < 7) {
			setCreatedAt(targetDate.fromNow())
		} else {
			setCreatedAt(targetDate.format('YYYY-MM-DD'))
		}
		setWordTappedForDeletion(false)
	}, [data?.created_at])

	useEffect(() => {
		setShowAllCollections(false)
		setWordTappedForDeletion(false)
		setNewWordCollectionIds([])
	}, [isOpen])

	useEffect(() => {
		if (mode === 'add') {
			setNewWord('')
			setNewWordTranslation('')
		}
	}, [mode])

	useEffect(() => {
		if (prevNewWord.current !== '' && newWord === '') {
			setNewWordTranslation('')
			setTranslationSuggestions([])
		}
		prevNewWord.current = newWord
	}, [newWord])

	const resetAddWordInputs = () => {
		setNewWord('')
		setNewWordTranslation('')
		setTranslationSuggestions([])
		setNewWordCollectionIds([])
	}

	const closeDialog = () => {
		resetAddWordInputs()
		hideModal()
	}

	useBus('close-modal', () => {
		hideModal()
	})

	const deleteWord = async () => {
		setIsDeleting(true)
		await api(`/v1/library/vocabulary/${data?.id}`, {
			method: 'DELETE',
		})
			.then(() => closeDialog())
			.catch(() => toast(t('Unexpected error')))
			.finally(() => setIsDeleting(false))
		if (onSuccessfulFinalAction !== null) {
			onSuccessfulFinalAction(OnSuccessfulFinalActionMode.DELETE)
		}
		// allow some time for the server to update the database before reloading the collections
		setTimeout(() => {
			for (const i of data?.collections ?? []) {
				dispatch('reload-open-collection', {
					collectionId: i.id,
				})
			}
		}, 1000)
	}

	const submitWord = async () => {
		if (mode.includes('add')) {
			await submitNewWord()
		} else if (mode.includes('edit')) {
			await submitEditWord()
		}
	}

	const submitNewWord = async () => {
		if (isTrialExpired) {
			dispatch('show-auth-dialog', {
				trialExpired: true,
			})
			return
		}
		setIsSubmittingNewWord(true)
		await api(`/v1/library/vocabulary/`, {
			method: 'POST',
			body: {
				word: newWord,
				translation: newWordTranslation,
				learning_language_code: learningLanguageCode,
				app_language_code: appLanguageCode,
				library_collection_id: newWordCollectionIds,
			},
		})
			.then((libVoc: LibraryVocabulary) => {
				if (onSuccessfulFinalAction !== null) {
					onSuccessfulFinalAction(OnSuccessfulFinalActionMode.ADD, libVoc)
				}
				closeDialog()
			})
			.catch((x) => {
				// todo: show upgrade page
				toast(x.data?.message ?? t('Something went wrong'))
			})
			.finally(() => setIsSubmittingNewWord(false))
	}

	const submitEditWord = async () => {
		setIsSubmittingNewWord(true)
		await api(`/v1/library/vocabulary/${data?.id}`, {
			method: 'PUT',
			body: {
				word: newWord,
				translation: newWordTranslation,
			},
		})
			.then((libVoc: LibraryVocabulary) => {
				setNewWord('')
				setNewWordTranslation('')
				if (onSuccessfulFinalAction !== null) {
					onSuccessfulFinalAction(OnSuccessfulFinalActionMode.EDIT, libVoc)
					for (const i of libVoc.collections) {
						dispatch('reload-open-collection', {
							collectionId: i.id,
						})
					}
				}
				setData(libVoc)
				setMode('saved')
			})
			.catch((x) => {
				toast(x.data?.message ?? t('Something went wrong'))
			})
			.finally(() => setIsSubmittingNewWord(false))
	}

	const toggleCollectionLink = async (collectionId: number) => {
		if (data?.id) {
			setLoadingCollectionIds((prevIds) => [...prevIds, collectionId])
			await api(`/v2/library/collections/${collectionId}/LibraryVocabulary/${data?.id}`, {
				method: 'POST',
			})
				.then(async (x: LibraryVocabulary) => {
					setData(x)
					await loadLibraryOverview()
					await loadLibraryCollectionsList()
					dispatch('reload-open-collection', {
						collectionId: collectionId,
					})
				})
				.catch(() => toast(t('Unexpected error')))
				.finally(() =>
					setLoadingCollectionIds((prevIds) => prevIds.filter((itemId) => itemId !== collectionId)),
				)
		} else {
			if (newWordCollectionIds.includes(collectionId)) {
				setNewWordCollectionIds((prevIds) => prevIds.filter((itemId) => itemId !== collectionId))
			} else {
				setNewWordCollectionIds((prevIds) => [...prevIds, collectionId])
			}
		}
	}

	const fetchAiTranslation = async () => {
		setIsLoadingAiTranslation(true)
		await api('/v2/library/translate', {
			method: 'GET',
			query: {
				word: newWord,
				learning_language_code: learningLanguageCode,
				app_language_code: appLanguageCode,
			},
		})
			.then(({ translations }: { translations: string[] }) => {
				if (translations.length !== 1 && translations[0] !== newWord) {
					setTranslationSuggestions(translations)
				} else if (translations.length === 1) {
					setNewWordTranslation(translations[0])
				}
			})
			.catch((x) => {
				toast(x.data?.message ?? t('Something went wrong'))
			})
			.finally(() => setIsLoadingAiTranslation(false))
	}

	const tappedForEdit = () => {
		setMode('edit')
		if (data !== null) {
			setNewWord(data.word)
			setNewWordTranslation(data.translation ?? '')
		}
	}

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					hideModal()
				}
			}}>
			<DialogContent
				dir={availableAppLanguages[appLanguageCode]?.dir}
				hideClose
				className="p-3 w-[95%] rounded-lg gap-1">
				<VisuallyHidden.Root>
					<DialogTitle>Word details dialog</DialogTitle>
					<SheetDescription>Word details dialog</SheetDescription>
				</VisuallyHidden.Root>
				<div className="text-sm text-left flex items-center justify-between">
					<span className="flex items-center">
						<BookmarkCheck className="inline-flex w-4 me-1" />
						{mode.includes('saved') ? t('Saved vocabulary') : t('Save word or term')}
					</span>
					<Button onClick={closeDialog} variant="ghost" size="icon" className="w-7 h-7">
						<X className="w-4" />
					</Button>
				</div>
				{mode.includes('saved') && (
					<div className="flex !m-0">
						<small>
							{data?.is_ai_recommended ? t('Saved from a chat on') : t('Saved manually on')}:
							<span className="ms-1">{createdAt}</span>
						</small>
					</div>
				)}
				<div className="!m-0">
					{mode.includes('add') && (
						<div className="my-3">
							<p className="text-start text-xs mb-4">
								<Trans
									i18nKey="Tap <icon key={1} /> to get automatic dictionary translation suggestions for the word or term entered below."
									components={{
										icon: <Sparkles className="h-4 w-4 inline mx-1" />,
									}}
								/>
							</p>
						</div>
					)}
					<div className={`flex justify-between ${mode === 'saved' ? 'my-2' : ''}`}>
						<div className="my-2 flex-grow">
							<div className="flex justify-between">
								{mode.includes('saved') ? (
									<h4 className="flex text-xl font-normal items-center justify-start">
										{data?.word}
									</h4>
								) : (
									<div className="relative w-full me-1">
										<FloatingInput
											tabIndex={1}
											// autoCapitalize="none"
											autoFocus={newWord?.length === 0}
											autoComplete="off"
											className="!outline-none !ring-0 text-base"
											id="new-word"
											value={newWord}
											onChange={(val) => setNewWord(val.target.value)}
										/>
										<FloatingLabel htmlFor="new-word" className="text-muted-foreground">
											{t('Word / Term')}
										</FloatingLabel>
									</div>
								)}
								<Button
									tabIndex={3}
									onClick={() =>
										pronounceText(data !== null && mode.includes('saved') ? data.word : newWord)
									}
									variant="ghost"
									className={cn(!isTTSSupported ? 'opacity-50' : '')}
									size="icon">
									<Volume2 className="h-5 w-5" />
								</Button>
							</div>
							<div className={`flex justify-between ${mode.includes('saved') ? '' : 'mt-3'}`}>
								{mode.includes('saved') ? (
									<h5 className="flex text-muted-foreground items-center text-start">
										{data?.translation}
									</h5>
								) : (
									<div className="relative w-full me-1">
										<SuggestionFloatingInput
											tabIndex={2}
											label={t('Translation')}
											autoCapitalize="none"
											autoComplete="off"
											onKeyDown={async (e: any) => {
												if (e.key === 'Enter') await submitWord()
											}}
											className="!outline-none !ring-0 text-base"
											id="new-word-translation"
											value={newWordTranslation}
											onChange={(val: any) => setNewWordTranslation(val.target.value)}
											suggestions={translationSuggestions}
											onSuggestionClicked={(x) => {
												setNewWordTranslation(x)
												setTranslationSuggestions([])
											}}
										/>
									</div>
								)}
								{(mode.includes('add') || mode.includes('edit')) && !mode.includes('saved') && (
									<Button
										disabled={isLoadingAiTranslation || newWord.length <= 0}
										tabIndex={3}
										onClick={fetchAiTranslation}
										variant="ghost"
										size="icon">
										{isLoadingAiTranslation ? (
											<Loader2 className="h-4 w-4 animate-spin" />
										) : (
											<Sparkles className="h-5 w-5" />
										)}
									</Button>
								)}
							</div>
						</div>
					</div>
					{mode.includes('saved') && (
						<>
							<hr />
							<div className="my-2">
								<div className="flex items-center">
									<div className="flex flex-none w-14">
										{data !== null && <LearningStageCircle learningStage={data.learning_stage} />}
									</div>
									<div className="flex flex-col text-start ms-2">
										<span className="text-sm">{description}</span>
										<div className="text-sm font-light">
											{t('Practice count')}:<span className="ms-1">{data?.practiced_count}</span>
										</div>
										<div className="text-sm font-light">
											{t('Last practice')}:
											<span className="ms-1">
												{data?.last_practiced_at !== null
													? dayjs().to(dayjs.utc(data?.last_practiced_at).local())
													: t('-')}
											</span>
										</div>
									</div>
								</div>
							</div>
						</>
					)}
					<div className="mt-2 flex justify-between items-center">
						{mode === 'saved/no-edit' ? (
							<small className="text-left flex items-center text-gray-500">
								<Info className="w-4 h-4 me-2" />
								{t('Edit or delete from your library')}
							</small>
						) : (
							<>
								{!wordTappedForDeletion && (
									<div className="flex items-center">
										<Shapes className="h-4 w-4 me-1" />
										<span className="text-sm">{t('Collections')}</span>
									</div>
								)}
								{mode.includes('saved') ? (
									<div>
										<Button
											className="me-1"
											disabled={isDeleting}
											variant={wordTappedForDeletion ? 'destructive' : 'ghost'}
											onClick={() => setWordTappedForDeletion(true)}>
											{!isDeleting && (
												<Trash className={cn('w-4', wordTappedForDeletion ? 'me-2' : '')} />
											)}
											{wordTappedForDeletion &&
												(!isDeleting ? (
													<span onClick={deleteWord}>{t('Tap to remove')}</span>
												) : (
													<Loader2 className="h-4 w-4 animate-spin" />
												))}
										</Button>
										{!wordTappedForDeletion && (
											<Button onClick={() => tappedForEdit()}>
												<Pencil className="w-4 me-2" />
												<span>{t('Edit')}</span>
											</Button>
										)}
									</div>
								) : (
									<Button
										onClick={submitWord}
										tabIndex={4}
										disabled={
											isSubmittingNewWord ||
											newWord?.length <= 0 ||
											newWordTranslation?.length <= 0
										}
										variant="outline">
										{isSubmittingNewWord ? (
											<Loader2 className="h-4 w-4 animate-spin" />
										) : (
											<FilePlus2 className="h-4 w-4" />
										)}
										<span className="ms-2">{t('Save')}</span>
									</Button>
								)}
								{wordTappedForDeletion && (
									<Button variant="outline" onClick={() => setWordTappedForDeletion(false)}>
										<Undo2 className="me-2 w-5" />
										{t('Cancel')}
									</Button>
								)}
							</>
						)}
					</div>
				</div>
				{!wordTappedForDeletion && (
					<div className="select-none">
						<div>
							{visibleCollections.map((collection) => {
								const checked = [...newWordCollectionIds, ...selectedCollectionIds].includes(
									collection.id,
								)
								const id = `collection-checkbox-${collection.id}`
								return (
									<div className="flex items-center mb-2" key={collection.id}>
										{loadingCollectionIds.includes(collection.id) ? (
											<Loader2 className="h-4 w-4 animate-spin" />
										) : (
											<Checkbox
												id={id}
												checked={checked}
												onCheckedChange={() => toggleCollectionLink(collection.id)}
											/>
										)}
										<label
											htmlFor={id}
											className="mx-2 cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
											{collection.name}
										</label>
									</div>
								)
							})}
							{hasMoreCollections && (
								<div className="mt-2">
									<Button variant="ghost" size="sm" onClick={() => setShowAllCollections((v) => !v)}>
										{showAllCollections ? t('Show less') : t('Show more')}
									</Button>
								</div>
							)}
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default WordDetailsDialog
